// @ts-check

import {
  Card,
  Col,
  Row,
  Slider,
  Space,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import axios from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { BiWind } from "react-icons/bi";
import { FaCloudRain, FaSun } from "react-icons/fa";
import { useMutation, useQueryClient } from "react-query";
import tr from "../i18n";
import useWindowSize from "../utils/WindowSize";
import { useGarden } from "./garden-context";

const optimizationLabels = {
  rain: tr("Rain Probability"),
  sun: tr("Temperature"),
  wind: tr("Wind Speed"),
};

const optimizationLabelKeys = {
  rain: "Rain Forecast",
  sun: "High Temperature",
  wind: "High Winds",
};

const optimizationMessageKeys = {
  rain: "Rain rule",
  sun: "Temperature rule",
  wind: "Wind rule",
};

const renderWithBoldValue = (text, value, valueColor) => {
  const parts = text.split(/{{\s*value\s*}}/);

  return (
    <>
      {parts[0]}
      <Typography.Text strong style={{ color: valueColor, fontSize: 12 }}>
        {value}
      </Typography.Text>
      {parts[1]}
    </>
  );
};

export const GARDEN_OPTIMIZATION_CHANGE_EVENT = "garden-optimization-change";

/** @typedef {number | [number, number]} SliderValue */

const maxFieldNameByOptimizationName =
  /** @type {Record<import("../types/global").OptimizationType, Parameters<import("../types/global").handleOptimizationSliderChangeFunction>[0]>} */ ({
    rain: "maxRain",
    sun: "maxSun",
    wind: "maxWind",
  });

const OptimizationsContext =
  /** @type {import("react").Context<import("../types/global").OptimizationsContextValue | undefined>} */ (
    createContext(undefined)
  );

export function useOptimization() {
  const context =
    /** @type {import("../types/global").OptimizationsContextValue | undefined} */ (
      useContext(OptimizationsContext)
    );
  if (!context)
    throw new Error(
      "useOptimization must be used within an OptimizationProvider",
    );
  return context;
}

/**
 * @param {import("react").PropsWithChildren<{}>} props
 * @returns {import("react").ReactElement | null}
 */
export function OptimizationProvider({ children }) {
  const { garden } = useGarden();
  const queryClient = useQueryClient();
  const { width } = useWindowSize();

  const { mutate: mutateOptimization, isLoading: isMutatingOptimization } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<Extract<import("../types/global").GardenOptimizeResponse, {success:true}>>, import("axios").AxiosResponse<Extract<import("../types/global").GardenOptimizeResponse, {success: false}>>, import("../types/global").GardenOptimizeRequest>} */ (
      useMutation(
        (request) =>
          axios.post(
            `${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/optimize`,
            request,
          ),
        {
          onMutate: async (newOptimization) => {
            // Cancel any outgoing refetches to prevent race conditions
            await queryClient.cancelQueries([
              "Garden",
              newOptimization.gardenId,
            ]);

            // Snapshot the previous value for rollback
            const previousGarden = queryClient.getQueryData([
              "Garden",
              newOptimization.gardenId,
            ]);

            // Optimistically update the cache
            // @ts-ignore
            queryClient.setQueryData(
              ["Garden", newOptimization.gardenId],
              (old) => {
                if (!old?.data?.garden) return old;
                return {
                  ...old,
                  data: {
                    ...old.data,
                    garden: {
                      ...old.data.garden,
                      optimization: {
                        ...old.data.garden.optimization,
                        ...newOptimization.optimize,
                      },
                    },
                  },
                };
              },
            );

            return { previousGarden, gardenId: newOptimization.gardenId };
          },
          onError: (err, newOptimization, context) => {
            console.error("Optimization mutation failed:", err);

            // Rollback to previous state on error
            if (context?.previousGarden) {
              queryClient.setQueryData(
                ["Garden", context.gardenId],
                context.previousGarden,
              );
            }

            // Show error notification in development
            if (process.env.NODE_ENV === "development") {
              console.warn("Optimization mutation variables:", newOptimization);
            }
          },
          onSuccess: (data, variables) => {
            // Update cache with server response if available
            // The optimization endpoint might not return the full garden data
            // so we'll let the invalidateQueries handle the refresh
            if (process.env.NODE_ENV === "development") {
              console.log("Optimization mutation successful:", data);
            }
          },
          onSettled: (data, error, variables) => {
            // Always invalidate to ensure consistency
            queryClient.invalidateQueries(["Garden", variables.gardenId]);
          },
        },
      )
    );

  const handleOptimizationChange = useCallback(
    /**
     *
     * @param {string} field
     * @param {import("../types/global").Garden["id"]} gardenId
     * @param {boolean} value
     * @returns
     */
    (field, gardenId, value) => {
      if (width) {
        const isMobileScreen = width <= 768;
        if (isMobileScreen) {
          const ok = window.confirm(
            tr("Do you want to change the optimization settings?"),
          );
          if (!ok) return;
        }
      }

      mutateOptimization({ gardenId, optimize: { [field]: value } });
    },
    [mutateOptimization, width],
  );

  const handleOptimizationSliderChange = useCallback(
    /**
     * @param {Parameters<import("../types/global").handleOptimizationSliderChangeFunction>[0]} field
     * @param {Parameters<import("../types/global").handleOptimizationSliderChangeFunction>[1]} gardenId
     * @param {Parameters<import("../types/global").handleOptimizationSliderChangeFunction>[2]} value
     * @returns {void}
     */
    (field, gardenId, value) => {
      mutateOptimization({ gardenId, optimize: { [field]: value } });
    },
    [mutateOptimization],
  );

  const optimizationContextValue = useMemo(() => {
    /** @type {import("../types/global").OptimizationsContextValue} */
    const _ = {
      handleOptimizationChange,
      handleOptimizationSliderChange,
      isMutatingOptimization,
      optimization: garden?.optimization,
    };

    return _;
  }, [
    garden?.optimization,
    handleOptimizationChange,
    handleOptimizationSliderChange,
    isMutatingOptimization,
  ]);

  return (
    <OptimizationsContext.Provider value={optimizationContextValue}>
      {children}
    </OptimizationsContext.Provider>
  );
}

/**
 *
 * @param {import("react").PropsWithChildren<{optimizationName: import("../types/global").OptimizationType}>} props
 * @returns {import("react").ReactElement | null}
 */
OptimizationProvider.Card = function OptimizationProviderCard({
  children,
  optimizationName,
}) {
  const { gardenId, isOptimizationsPanelOpened } = useGarden();
  const { handleOptimizationChange, optimization } = useOptimization();

  if (!optimization) return null;
  if (!gardenId) return null;
  if (!isOptimizationsPanelOpened) return null;
  const labelKey = optimizationLabelKeys[optimizationName];

  return !isOptimizationsPanelOpened ? null : (
    <Card
      size="small"
      style={{
        backgroundColor: optimization[optimizationName]
          ? "mintcream"
          : "whitesmoke",
        borderRadius: 12,
      }}
      title={
        <Row>
          <Col flex={1}>
            <Space align="center">
              <OptimizationProvider.Icon optimizationName={optimizationName} />

              <Typography.Text>
                {tr(labelKey || optimizationName)}
              </Typography.Text>
            </Space>
          </Col>

          <Col>
            <Switch
              checked={optimization[optimizationName]}
              onChange={(checked) =>
                handleOptimizationChange(optimizationName, gardenId, checked)
              }
              size="small"
            />
          </Col>
        </Row>
      }
    >
      {children}
    </Card>
  );
};

/**
 *
 * @param {Object} props
 * @param {import("../types/global").OptimizationType} props.optimizationName
 * @param {number} [props.size=16]
 * @param {import("react").StyleHTMLAttributes<import("leaflet").DivIcon>["color"]} [props.color] - Optional color override for the icon
 * @returns {import("react").ReactElement | null}
 */
OptimizationProvider.Icon = function OptimizationProviderIcon({
  color,
  optimizationName,
  size = 16,
}) {
  const { optimization } = useOptimization();

  switch (optimizationName) {
    case "sun":
      return (
        <FaSun
          size={size}
          style={{
            color: color ? color : optimization?.sun ? "orange" : "GrayText",
          }}
        />
      );
    case "rain":
      return (
        <FaCloudRain
          size={size}
          style={{
            color: color
              ? color
              : optimization?.rain
                ? "lightskyblue"
                : "GrayText",
          }}
        />
      );
    case "wind":
      return (
        <BiWind
          size={size}
          style={{
            color: color
              ? color
              : optimization?.wind
                ? "blueviolet"
                : "GrayText",
          }}
        />
      );
    default:
      return null;
  }
};

/**
 * Update switch for optimization of garden
 * @param {import("antd").SwitchProps} props
 * @returns {import("react").ReactElement | null}
 */
OptimizationProvider.Switch = function OptimizationProviderSwitch(props) {
  const { isOptimizationsPanelOpened, updateIsOptimizationsPanelOpened } =
    useGarden();

  return (
    <Switch
      checked={isOptimizationsPanelOpened}
      onChange={updateIsOptimizationsPanelOpened}
      size="small"
      {...props}
    />
  );
};

/**
 *
 * @param {Object} props
 * @param {number} props.maximum
 * @param {number} props.minimum
 * @param {import("../types/global").OptimizationType} props.optimizationName
 * @param {"celsius" | "kilometer-per-hour" | "percent"} props.unit
 * @returns {import("react").ReactElement | null}
 */
OptimizationProvider.Slider = function OptimizationProviderSlider({
  maximum,
  minimum,
  optimizationName,
  unit: defaultUnit,
}) {
  const { handleOptimizationSliderChange, optimization } = useOptimization();
  const { gardenId } = useGarden();

  const [locale] = useState(localStorage.getItem("locale") || "tr");

  /* -------------------------------------------------- */
  /* Conversion Helpers                                 */
  /* -------------------------------------------------- */

  /**
   * @param {number | undefined} val
   * @returns {number | undefined}
   */
  const convertForDisplay = (/** @type {number | undefined} */ val) => {
    if (locale === "en" && val !== undefined) {
      if (defaultUnit === "celsius") return (val * 9) / 5 + 32;
      if (defaultUnit === "kilometer-per-hour") return val / 1.60934;
    }
    return val;
  };

  /**
   * @param {number | undefined} val
   * @returns {number}
   */
  const convertToMetric = (/** @type {number | undefined} */ val) => {
    if (locale === "en" && val !== undefined) {
      if (defaultUnit === "celsius") return Math.round(((val - 32) * 5) / 9);
      if (defaultUnit === "kilometer-per-hour")
        return Math.round(val * 1.60934);
    }
    return Math.round(val ?? 0); // ensures integer even if val is undefined/null
  };

  /* -------------------------------------------------- */
  /* IMPORTANT: Hooks must be declared before return    */
  /* -------------------------------------------------- */

  const metricValue = optimization?.limits?.max?.[optimizationName] ?? minimum;

  const [currentValue, setCurrentValue] = useState(() =>
    convertForDisplay(metricValue),
  );

  /* -------------------------------------------------- */
  /* Now it's safe to early return                      */
  /* -------------------------------------------------- */

  if (!gardenId || !optimization) return null;

  const maxFieldName =
    /** @type {Parameters<import("../types/global").handleOptimizationSliderChangeFunction>[0]} */ (
      maxFieldNameByOptimizationName[optimizationName]
    );

  const sliderMin = locale === "en" && defaultUnit === "celsius" ? 75 : minimum;

  const sliderMax =
    locale === "en" && defaultUnit === "celsius" ? 115 : maximum;

  const displayUnit =
    locale === "en"
      ? defaultUnit === "celsius"
        ? "fahrenheit"
        : defaultUnit === "kilometer-per-hour"
          ? "mile-per-hour"
          : defaultUnit
      : defaultUnit;

  /**
   * @param {number | undefined} val
   * @returns {string}
   */
  const formatValue = (/** @type {number | undefined} */ val) => {
    if (val === undefined || val === null) return "";

    if (displayUnit === "percent") {
      return Intl.NumberFormat(locale, {
        style: "percent",
        maximumFractionDigits: 0,
      }).format(val / 100);
    }

    if (displayUnit === "fahrenheit") {
      return `${Math.round(val)}°F`;
    }

    return Intl.NumberFormat(locale, {
      style: "unit",
      unit: displayUnit,
      unitDisplay: "narrow",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const sliderMarks = {
    [sliderMax]: (
      <Typography.Text
        style={{
          fontSize: 12,
          whiteSpace: "nowrap",
        }}
      >
        {formatValue(sliderMax)}
      </Typography.Text>
    ),
    [sliderMin]: (
      <Typography.Text
        style={{
          fontSize: 12,
          whiteSpace: "nowrap",
        }}
      >
        {formatValue(sliderMin)}
      </Typography.Text>
    ),
  };

  const messageKey = optimizationMessageKeys[optimizationName];

  const rawText = messageKey ? tr(messageKey) || messageKey : optimizationName;

  return (
    <Row>
      <Col span={24}>
        <Space>
          <Typography.Text style={{ color: "GrayText", fontSize: 12 }}>
            {renderWithBoldValue(rawText, formatValue(currentValue))}
          </Typography.Text>
        </Space>
      </Col>

      <Col span={24} style={{ padding: "0 12px" }}>
        <Slider
          value={currentValue}
          disabled={!optimization[optimizationName]}
          max={sliderMax}
          min={sliderMin}
          marks={sliderMarks}
          onChange={
            /**
             * @param {SliderValue} val
             */
            (val) => {
              if (typeof val === "number") {
                setCurrentValue(val);
              }
            }
          }
          onAfterChange={
            /**
             * @param {SliderValue} val
             */
            (val) => {
              if (typeof val === "number") {
                handleOptimizationSliderChange(
                  maxFieldName,
                  gardenId,
                  convertToMetric(val),
                );
              }
            }
          }
          tipFormatter={(val) => formatValue(val)}
        />
      </Col>
    </Row>
  );
};

/**
 *
 * @param {import("react").PropsWithChildren<{
 * maximum: number,
 * minimum: number,
 * optimizationName: import("../types/global").OptimizationType,
 * unit: "celsius" | "kilometer-per-hour" | "percent",
 * disabled?: boolean,
 * disabledMessage?: string,
 * dispabledMessage?: string,
 * }>} props
 * @returns {import("react").ReactElement | null}
 */
OptimizationProvider.CardAlternate = function OptimizationProviderCard({
  maximum,
  minimum,
  optimizationName,
  unit: defaultUnit,
  disabled = false,
  disabledMessage,
  dispabledMessage,
}) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [value, setValue] = useState(minimum);

  /**
   * @param {boolean} nextIsEnabled
   * @param {number} nextValue
   */
  const dispatchOptimizationChange = (nextIsEnabled, nextValue) => {
    window.dispatchEvent(
      new CustomEvent(GARDEN_OPTIMIZATION_CHANGE_EVENT, {
        detail: {
          optimizationName,
          isEnabled: nextIsEnabled,
          value: nextValue,
        },
      }),
    );
  };

  const [locale] = useState(localStorage.getItem("locale") || "tr");

  const convertForDisplay = (/** @type {number | undefined} */ val) => {
    if (locale === "en" && val !== undefined) {
      if (defaultUnit === "celsius") return (val * 9) / 5 + 32; // Celsius -> Fahrenheit
      if (defaultUnit === "kilometer-per-hour") return val / 1.60934; // km/h -> mph
    }
    return val;
  };

  const metricValue = minimum;

  const [currentValue, setCurrentValue] = useState(() =>
    convertForDisplay(metricValue),
  );
  const disabledTextColor = "rgba(0, 0, 0, 0.25)";
  const descriptiveTextColor = disabled ? disabledTextColor : "GrayText";
  const resolvedDisabledMessage =
    disabledMessage ||
    dispabledMessage ||
    tr("This optimization is currently unavailable.");

  const sliderMin = locale === "en" && defaultUnit === "celsius" ? 75 : minimum;

  const sliderMax =
    locale === "en" && defaultUnit === "celsius" ? 115 : maximum;

  const displayUnit =
    locale === "en"
      ? defaultUnit === "celsius"
        ? "fahrenheit"
        : defaultUnit === "kilometer-per-hour"
          ? "mile-per-hour"
          : defaultUnit
      : defaultUnit;

  /**
   * @param {number | undefined} val
   * @returns {string}
   */
  const formatValue = (/** @type {number | undefined} */ val) => {
    if (val === undefined || val === null) return "";

    if (displayUnit === "percent") {
      return Intl.NumberFormat(locale, {
        style: "percent",
        maximumFractionDigits: 0,
      }).format(val / 100);
    }

    if (displayUnit === "fahrenheit") {
      return `${Math.round(val)}°F`;
    }

    return Intl.NumberFormat(locale, {
      style: "unit",
      unit: displayUnit,
      unitDisplay: "narrow",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const sliderMarks = {
    [sliderMax]: (
      <Typography.Text
        style={{
          color: disabled ? disabledTextColor : undefined,
          fontSize: 12,
          whiteSpace: "nowrap",
        }}
      >
        {formatValue(sliderMax)}
      </Typography.Text>
    ),
    [sliderMin]: (
      <Typography.Text
        style={{
          color: disabled ? disabledTextColor : undefined,
          fontSize: 12,
          whiteSpace: "nowrap",
        }}
      >
        {formatValue(sliderMin)}
      </Typography.Text>
    ),
  };
  const labelKey = optimizationLabelKeys[optimizationName];
  const messageKey = optimizationMessageKeys[optimizationName];

  const rawText = messageKey ? tr(messageKey) || messageKey : optimizationName;

  const card = (
    <Card
      size="small"
      style={{
        backgroundColor: isEnabled ? "mintcream" : "whitesmoke",
        borderRadius: 12,
        cursor: disabled ? "not-allowed" : "default",
      }}
      title={
        <Row>
          <Col flex={1}>
            <Space align="center">
              <OptimizationProvider.Icon optimizationName={optimizationName} />

              <Typography.Text
                disabled={disabled}
                style={{ color: disabled ? disabledTextColor : undefined }}
              >
                {tr(labelKey || optimizationName)}
              </Typography.Text>
            </Space>
          </Col>

          <Col>
            <Switch
              disabled={disabled}
              checked={isEnabled}
              onChange={(checked) => {
                setIsEnabled(checked);
                dispatchOptimizationChange(checked, value);
              }}
              size="small"
            />
          </Col>
        </Row>
      }
    >
      <Row>
        <Col span={24}>
          <Space>
            <Typography.Text
              disabled={disabled}
              style={{ color: descriptiveTextColor, fontSize: 12 }}
            >
              {renderWithBoldValue(
                rawText,
                formatValue(currentValue),
                disabled ? disabledTextColor : undefined,
              )}
            </Typography.Text>
          </Space>
        </Col>

        <Col span={24} style={{ padding: "0 12px" }}>
          <Slider
            value={currentValue}
            disabled={disabled || !isEnabled}
            max={sliderMax}
            min={sliderMin}
            marks={sliderMarks}
            onChange={
              /**
               * @param {SliderValue} val
               */
              (val) => {
                if (typeof val === "number") {
                  setValue(val);
                  setCurrentValue(val);
                  dispatchOptimizationChange(isEnabled, val);
                }
              }
            }
            onAfterChange={(val) =>
              console.log(
                "Slider value changed for",
                optimizationName,
                "new value:",
                val,
              )
            } // send metric
            tipFormatter={(val) => formatValue(val)} // show imperial/°F while sliding
          />
        </Col>
      </Row>
    </Card>
  );

  return (
    <Tooltip title={disabled ? resolvedDisabledMessage : null}>
      <div style={{ width: "100%" }}>{card}</div>
    </Tooltip>
  );
};
